import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import LogoMarkSvg from '../../../assets/logos/logo-mark.svg?react';
import {
  nonSelectableSurfaceStyles,
  PrimaryButton,
  rubikTypographyStyles,
} from '../../../shared/ui';

import { TipsShareQrCode } from './tips-share-qr-code';

const Content = styled.div.attrs({
  'data-ui': 'tips-share-qr-fullscreen-content',
})`
  ${nonSelectableSurfaceStyles};
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.scale.px(28)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(40)}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(94)}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(40)}, env(safe-area-inset-left))`};
  box-sizing: border-box;
`;

const Hero = styled.div.attrs({
  'data-ui': 'tips-share-qr-fullscreen-hero',
})`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BrandBlock = styled.div`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(6)};
  margin-bottom: ${({ theme }) => theme.scale.px(32)};
`;

const LogoMark = styled(LogoMarkSvg).attrs({ 'aria-hidden': true })`
  width: ${({ theme }) => theme.scale.px(158)};
  height: ${({ theme }) => theme.scale.px(50)};
  color: ${({ theme }) => theme.colors.text.primary};
  --logo-mark-accent: ${({ theme }) => theme.colors.brand.primary};
`;

const BrandTagline = styled.p`
  margin: 0;
  font-family: 'Baloo 2', sans-serif;
  font-size: ${({ theme }) => theme.scale.px(11)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const QrCode = styled(TipsShareQrCode).attrs({
  size: 214,
})`
  margin-bottom: ${({ theme }) => theme.scale.px(30)};
`;

const Title = styled.h1`
  ${rubikTypographyStyles};
  margin: 0;
  width: ${({ theme }) => theme.scale.px(264)};
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(22)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(26)};
  white-space: pre-line;
`;

const CloseButton = styled(PrimaryButton).attrs({
  'data-ui': 'tips-share-qr-fullscreen-close-button',
  type: 'button',
})`
  width: 100%;
  border-radius: ${({ theme }) => theme.scale.px(12)};
`;

type TipsShareQrFullscreenContentProps = {
  onClose: () => void;
};

export function TipsShareQrFullscreenContent({
  onClose,
}: TipsShareQrFullscreenContentProps) {
  const { t } = useTranslation();

  return (
    <Content>
      <Hero>
        <BrandBlock>
          <LogoMark />
          <BrandTagline>{t('homeDashboard.brandTagline')}</BrandTagline>
        </BrandBlock>

        <QrCode />

        {/* Audit note: Intentional reuse of `homeDashboard.brandTagline` for both BrandTagline and Title per current design copy. */}
        <Title>{t('homeDashboard.brandTagline')}</Title>
      </Hero>

      <CloseButton onClick={onClose}>{t('homeDashboard.closeQr')}</CloseButton>
    </Content>
  );
}
